import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { useSliderDotButton } from '@/components/shared/slider/useSliderDotButton';
import { Box, Flex, ResponsiveBreak, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SliderDotButton, SliderDotButtonWrapper } from '../shared/slider/SliderDotButton';
import { CheckMarkIcon, PetLandingPageType } from './shared';

const WhatIsCoveredWrapper = styled(Section)`
  width: 100%;
`;

const Heading = styled(SectionTitle)`
  margin-bottom: 24px;
  text-transform: capitalize;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 16px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 32px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 80px;
    `}
`;

export const MainCTA = styled(JoinCTA)`
  width: 265px;
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 48px;
    `}
`;

const Overlay = styled.div<{ isLastSection: boolean }>`
  position: absolute;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0) 50%, #fff 99%);
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  display: ${({ isLastSection }) => (isLastSection ? 'none' : 'block')};
`;

const CoveredWrapper = styled(Flex)`
  display: none;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: flex;
    flex-wrap: wrap;
    padding: 0 10%;
  `}
`;

const CoveredSection = styled(Flex).attrs({ flexDirection: 'column' })`
  flex: 1 0 auto;

  @media (max-width: 1380px) {
    flex: 1 0 50%;
  }
`;

const CoveredItem = styled(Flex).attrs({ alignItems: 'center' })`
  ${font('main', { fontSize: '1.8rem', lineHeight: '1.3', fontWeight: 700 })};
  text-transform: capitalize;
  gap: 8px;
  margin-bottom: 24px;
  width: 250px;
`;

const DOG_COVERED = [
  ['bloodwork', 'x-rays', 'MRI', 'CT scans', 'lab work'],
  ['urinalysis', 'ultrasounds', 'cancer', 'emergency care', 'surgery'],
  ['heart disease', 'hospitalization', 'road accidents', 'outpatient care', 'allergy medicine'],
  ['specialty care', 'injections', 'prescription medications', 'arthritis medication', 'knee, elbow & hip dysplasia'],
];

const CAT_COVERED = [
  ['bloodwork', 'x-rays', 'MRI', 'CT scans', 'lab work'],
  ['urinalysis', 'ultrasounds', 'cancer', 'emergency care', 'surgery'],
  ['heart disease', 'hospitalization', 'road accidents', 'outpatient care', 'allergy medicine'],
  ['specialty care', 'injections', 'prescription medications', 'diabetes', 'urinary tract infections'],
];

const title = {
  [PetLandingPageType.Dog]: (
    <>
      Coverage For
      <ResponsiveBreak display={['block', 'inline']} />
      Today’s Dogs
    </>
  ),
  [PetLandingPageType.Cat]: (
    <>
      coverage They
      <ResponsiveBreak display={['block', 'inline']} />
      actually need
    </>
  ),
  [PetLandingPageType.PetInsurance]: (
    <>
      coverage They
      <ResponsiveBreak display={['block', 'inline']} />
      actually need
    </>
  ),
};

const subTitle = {
  [PetLandingPageType.Dog]: (
    <>
      Crafted by our crew of vet experts
      <ResponsiveBreak display={['block', 'inline']} />
      and dog-obsessed parents
    </>
  ),
  [PetLandingPageType.Cat]: (
    <>
      Designed by our team of vet
      <ResponsiveBreak display={['block', 'inline']} />
      experts and cat people
    </>
  ),
  [PetLandingPageType.PetInsurance]: (
    <>
      Designed by our team of vet
      <ResponsiveBreak display={['block', 'inline']} />
      experts and pet-obsessed parents
    </>
  ),
};

const Carousel: React.FC<{ covered: string[][]; setIsLastSection: (isLastSection: boolean) => void }> = ({
  covered,
  setIsLastSection,
}) => {
  const [emblaRef, slideApi] = useEmblaCarousel({
    containScroll: false,
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useSliderDotButton(slideApi);

  const logSlidesInView = useCallback((api: EmblaCarouselType) => {
    setIsLastSection(api?.selectedScrollSnap() === api?.scrollSnapList().length - 1);
  }, []);

  useEffect(() => {
    if (slideApi) slideApi.on('slidesInView', logSlidesInView);

    return () => {
      if (slideApi) slideApi.off('slidesInView', logSlidesInView);
    };
  }, [slideApi, logSlidesInView]);

  return (
    <Flex overflow="hidden" ref={emblaRef} flexDirection="column">
      <Flex gap="20px">
        {covered.map(section => (
          <Flex flex="0 0 50%" minWidth={0} flexDirection="column">
            {section.map(item => (
              <CoveredItem key={item}>
                <CheckMarkIcon />
                {item}
              </CoveredItem>
            ))}
          </Flex>
        ))}
      </Flex>

      <SliderDotButtonWrapper>
        {scrollSnaps.map((_, index) => (
          <SliderDotButton key={index} onClick={() => onDotButtonClick(index)} isActive={index === selectedIndex} />
        ))}
      </SliderDotButtonWrapper>
    </Flex>
  );
};

interface WhatIsCoveredProps {
  petLandingPageType: PetLandingPageType;
}

const WhatIsCovered: React.FC<WhatIsCoveredProps> = ({ petLandingPageType }) => {
  const [isLastSection, setIsLastSection] = useState(false);
  const covered = petLandingPageType === PetLandingPageType.Cat ? CAT_COVERED : DOG_COVERED;

  return (
    <WhatIsCoveredWrapper justifyContent="center" py={[40, 70]} pb={[40, 70]}>
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>{title[petLandingPageType]}</Heading>
        <SubHeading>{subTitle[petLandingPageType]}</SubHeading>
      </Flex>
      <CoveredWrapper>
        {covered.map((section, index) => (
          <CoveredSection key={index}>
            {section.map(coverItem => (
              <CoveredItem key={coverItem}>
                <CheckMarkIcon />
                {coverItem}
              </CoveredItem>
            ))}
          </CoveredSection>
        ))}
      </CoveredWrapper>
      <Box mt="50px" position="relative" width="100%" display={['block', 'none']}>
        <Overlay isLastSection={isLastSection} />
        <Carousel covered={covered} setIsLastSection={setIsLastSection} />
      </Box>
    </WhatIsCoveredWrapper>
  );
};

export default WhatIsCovered;
